import { ZonedDateTimeFactory, ZonedDateTimeFormatter } from '@csp/csp-common-date-util';
import {
  CustomStatus,
  CustomStatusIn,
  CustomStatusType,
  CustomStatusValue,
  CustomStatuses,
  Maybe,
  StatusValue,
  ZonedDateTime,
} from '@csp/csp-common-model';
import { StringUtil } from '@csp/csp-common-util';
import { CustomStatusV1 as AppointmentCustomStatusV1 } from '@csp/dmdp-api-appointment-dto';
import { PreviousCustomStatusV1, CustomStatusV1 as UserCustomStatusV1 } from '@csp/dmdp-api-common-dto';
import { CustomStatusV1 as DeviceSessionCustomStatusV1 } from '@csp/dmdp-api-device-dto';
import { CustomStatusV1 as BioSamplingCustomStatusV1 } from '@csp/dmdp-api-hbs-dto';
import { findLast, orderBy } from 'lodash';

type CustomStatusV1 =
  | BioSamplingCustomStatusV1
  | UserCustomStatusV1
  | (AppointmentCustomStatusV1 & {
      reason?: string;
    }) // Workaround until reason is available in Appointment API
  | DeviceSessionCustomStatusV1;

const toCustomStatusValue = (valueV1: PreviousCustomStatusV1 | CustomStatusV1): CustomStatusValue => {
  const localTimestamp = ZonedDateTimeFactory.fromZonedDateTimeString(valueV1.localTimestamp);
  const createdTimestamp = ZonedDateTimeFactory.fromZonedDateTimeString(valueV1.createdAt);

  // Only previous custom statuses can be invalidated
  const invalidated = 'invalidated' in valueV1 ? valueV1.invalidated : undefined;
  const invalidatedAt = 'invalidatedAt' in valueV1 ? valueV1.invalidatedAt : undefined;

  return {
    timestamp: localTimestamp ?? createdTimestamp,
    invalid: !!invalidated,
    invalidationDate: invalidatedAt ? ZonedDateTimeFactory.fromUnixTimeMillis(invalidatedAt, 'UTC') : undefined,
    localTimestamp,
    createdTimestamp,
    createdUserId: valueV1.createdBy,
    value: valueV1.value,
    reasonText: valueV1.reason,
  };
};

const toCustomStatusV1 = (customStatus: CustomStatus): CustomStatusV1 => ({
  type: customStatus.type,
  value: customStatus.value,
  createdAt: ZonedDateTimeFormatter.toDisplayDateTimeString(customStatus.createdTimestamp),
  createdBy: customStatus.createdUserId,
  localTimestamp: ZonedDateTimeFormatter.toZonedDateTimeString(customStatus.localTimestamp) ?? '',
  reason: customStatus.reasonText,
  previousCustomStatuses: customStatus.previousIncludingInvalid.map(previousStatus => ({
    value: previousStatus.value,
    createdAt: ZonedDateTimeFormatter.toDisplayDateTimeString(previousStatus.createdTimestamp),
    createdBy: previousStatus.createdUserId,
    localTimestamp: ZonedDateTimeFormatter.toZonedDateTimeString(previousStatus.localTimestamp) ?? '',
    reason: previousStatus.reasonText,
    invalidated: previousStatus.invalid,
  })),
});

const customStatusInToCustomStatusValue = (customStatusIn: CustomStatusIn): CustomStatusValue => ({
  timestamp: customStatusIn.timestamp ?? ZonedDateTimeFactory.now(),
  invalid: false,
  localTimestamp: customStatusIn.timestamp,
  createdTimestamp: ZonedDateTimeFactory.now('UTC'),
  createdUserId: undefined,
  value: customStatusIn.value,
  reasonText: customStatusIn.reasonText,
});

const fromCustomStatusIn = (customStatusIn: CustomStatusIn): CustomStatus => {
  const customStatusValue: CustomStatusValue = customStatusInToCustomStatusValue(customStatusIn);
  return toCustomStatus(customStatusIn.type, customStatusValue, []);
};

const fromCustomStatusIns = (customStatusIns: Readonly<CustomStatusIn[]>): CustomStatuses =>
  CustomStatusMapper.from(customStatusIns.map(CustomStatusMapper.fromCustomStatusIn));

const getSequentialValuesOnly = (
  currentValue: CustomStatusValue,
  previous: CustomStatusValue[],
): CustomStatusValue[] => {
  const valuesInSequence: CustomStatusValue[] = [];
  let compareValue: Maybe<CustomStatusValue> = undefined;
  [currentValue, ...previous].forEach(value => {
    if (
      !compareValue ||
      (compareValue.timestamp.unixTimeMillis >= value.timestamp.unixTimeMillis &&
        (compareValue.value !== value.value || value.invalid))
    ) {
      valuesInSequence.push(value);
      compareValue = value;
    }
  });
  return valuesInSequence;
};

const toCustomStatus = (
  type: CustomStatusType,
  currentValue: CustomStatusValue,
  previous: CustomStatusValue[],
): CustomStatus => {
  const previousIncludingInvalid = previous;

  const getPreviousExludingInvalid = (): CustomStatusValue[] =>
    previousIncludingInvalid.filter(value => !value.invalid);

  const getPreviousValue = (): Maybe<CustomStatusValue> => getPreviousExludingInvalid()[0];

  const getStatusValuesInSequenceNewestFirst = (): CustomStatusValue[] =>
    getSequentialValuesOnly(currentValue, getPreviousExludingInvalid());

  const getStatusValuesInSequenceOldestFirst = (): CustomStatusValue[] =>
    getStatusValuesInSequenceNewestFirst().slice().reverse();

  const getStatusValuesInSequenceOldestFirstIncludingInvalid = (): CustomStatusValue[] =>
    getSequentialValuesOnly(currentValue, previousIncludingInvalid).slice().reverse();

  const getSequentialValueByZonedDateTime = (zonedDateTime: ZonedDateTime): Maybe<CustomStatusValue> =>
    getStatusValuesInSequenceNewestFirst().find(value => value.timestamp.unixTimeMillis < zonedDateTime.unixTimeMillis);

  const getSequentialValueByNow = (): Maybe<CustomStatusValue> =>
    getSequentialValueByZonedDateTime(ZonedDateTimeFactory.now());

  const updateOptimistic = (customStatusIn: CustomStatusIn): CustomStatus => {
    const invalidateCurrent = !!customStatusIn.invalidateCurrent;
    const newCurrent = customStatusInToCustomStatusValue(customStatusIn);
    const newPrevious: CustomStatusValue[] = [
      {
        ...currentValue,
        invalid: invalidateCurrent,
        invalidationDate: invalidateCurrent ? newCurrent.createdTimestamp : undefined,
      },
      ...previousIncludingInvalid,
    ];
    return toCustomStatus(type, newCurrent, newPrevious);
  };

  return {
    type,
    ...currentValue,
    get previous(): CustomStatusValue[] {
      return getPreviousExludingInvalid();
    },
    previousIncludingInvalid,
    getPreviousValue,
    getStatusValuesInSequenceNewestFirst,
    getStatusValuesInSequenceOldestFirst,
    getStatusValuesInSequenceOldestFirstIncludingInvalid,
    getSequentialValueByZonedDateTime,
    getSequentialValueByNow,
    updateOptimistic,
  };
};

const isSameValueAndTimestamp = (val1: CustomStatusValue, val2: CustomStatusValue): boolean =>
  val1.timestamp.unixTimeMillis === val2.timestamp.unixTimeMillis && val1.value === val2.value;

const fromCustomStatusV1 = (statusV1: CustomStatusV1): CustomStatus => {
  const type = statusV1.type;

  const currentValue = toCustomStatusValue(statusV1);

  const previousValues = orderBy(
    statusV1.previousCustomStatuses?.map(prevStatusV1 => ({
      ...toCustomStatusValue(prevStatusV1),
    })) ?? [],
    status => status.createdTimestamp.unixTimeMillis,
    'desc',
  );
  const previousValuesWithInvalidationDates = previousValues.map((statusValue, i, allPrevious) => {
    if (statusValue.invalid && !statusValue.invalidationDate) {
      // 1. Is there a newer entry with the same value and timestamp?
      // Then that entry's created timestamp must be when this status was invalidated.
      const newerValues = [currentValue].concat(allPrevious.slice(0, i));
      const matchingValue = findLast(newerValues, newerValue => isSameValueAndTimestamp(statusValue, newerValue));

      // 2. If there is no matching value then it is the next entry's creation date is this
      // status invalidation date.
      const invalidationDate =
        matchingValue?.createdTimestamp ?? allPrevious[i - 1]?.createdTimestamp ?? currentValue.createdTimestamp;
      return {
        ...statusValue,
        invalidationDate,
      };
    } else {
      return statusValue;
    }
  });

  return toCustomStatus(type, currentValue, previousValuesWithInvalidationDates);
};

const fromCustomStatusesV1 = (customStatusesV1: CustomStatusV1[]): CustomStatuses =>
  CustomStatusMapper.from(customStatusesV1.map(fromCustomStatusV1));

const from = (statuses: Maybe<CustomStatus[]> = []): CustomStatuses => {
  const getByType = <Value = StatusValue>(type: CustomStatusType): Maybe<CustomStatus<Value>> =>
    statuses.find(status => status.type === type) as Maybe<CustomStatus<Value>>;

  const findOldestCustomStatusValue = (type: CustomStatusType, value: StatusValue): Maybe<CustomStatusValue> => {
    const customStatusWithMatchingType = getByType(type);
    const oldestPreviousValueMatch = findLast(
      customStatusWithMatchingType?.previous,
      statusValue => statusValue.value === value,
    );
    return (
      oldestPreviousValueMatch ??
      (customStatusWithMatchingType?.value === value ? customStatusWithMatchingType : undefined)
    );
  };

  const getCustomStatusValuesOldestFirst = (type: CustomStatusType, includeInvalid = false): CustomStatusValue[] => {
    const customStatusOfMatchingType = getByType(type);
    if (customStatusOfMatchingType) {
      const previous = includeInvalid
        ? customStatusOfMatchingType.previousIncludingInvalid
        : customStatusOfMatchingType.previous;
      return previous
        .slice()
        .reverse()
        .concat([
          {
            createdTimestamp: customStatusOfMatchingType.createdTimestamp,
            timestamp: customStatusOfMatchingType.timestamp,
            localTimestamp: customStatusOfMatchingType.localTimestamp,
            value: customStatusOfMatchingType.value,
            reasonText: customStatusOfMatchingType.reasonText,
            createdUserId: customStatusOfMatchingType.createdUserId,
            invalid: false,
            invalidationDate: undefined,
          },
        ]);
    } else {
      return [];
    }
  };

  const mutateOldestStatusTimestamp = (
    type: CustomStatusType,
    value: StatusValue,
    timestamp: Maybe<ZonedDateTime>,
  ): void => {
    const status = findOldestCustomStatusValue(type, value);
    if (status && timestamp) {
      status.timestamp = timestamp;
    }
  };

  const getValueByType = (type: CustomStatusType): Maybe<StatusValue> => getByType(type)?.value;

  const clone = (): CustomStatuses =>
    from(
      statuses.map(status =>
        toCustomStatus(
          status.type,
          {
            ...status,
          },
          status.previousIncludingInvalid.map(value => ({ ...value })),
        ),
      ),
    );

  const hash = StringUtil.deterministicStringify(statuses.map(status => status.getStatusValuesInSequenceNewestFirst()));

  return {
    statuses,
    hasStatuses: !!statuses.length,
    hash,
    getByType,
    getValueByType,
    findOldestCustomStatusValue,
    getCustomStatusValuesOldestFirst,
    mutateOldestStatusTimestamp,
    clone,
  };
};

const fromStatuses = (customStatuses: Maybe<CustomStatuses>): CustomStatuses => from(customStatuses?.statuses);

const empty = (): CustomStatuses => from([]);

export const CustomStatusMapper = {
  fromCustomStatusesV1,
  fromCustomStatusV1,
  fromCustomStatusIn,
  fromCustomStatusIns,
  from,
  empty,
  fromStatuses,
  toCustomStatus,
  toCustomStatusV1,
};
